import { useState, useEffect } from 'react';

import { LocationStateHandler } from 'src/utils/location-state-handler';

import { useI18n } from 'src/i18n';
import { CurvedMenu } from 'src/layouts/components/curved-menu';
import { Sidebar, SidebarBody } from 'src/layouts/components/sidebar';

import { Main } from './main';
import { NavContent } from './nav';
import { navData } from '../config-nav-dashboard';
import { LayoutSection } from '../core/layout-section';
import { FloatingHeader, useHeaderContent } from './header';
// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  className?: string;
  children: React.ReactNode;
  header?: {
    className?: string;
  };
};



export function DashboardLayout({ className, children, header }: DashboardLayoutProps) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [navOpen, setNavOpen] = useState(false);
  const [isDocked, setIsDocked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { isAuthenticated, leftHeaderContent, authButtons, authenticatedContent } =
    useHeaderContent();

  const mobileMenuButton = isMobile ? (
    <div
      onClick={() => setMenuOpen(true)}
      className="ml-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-accent"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setMenuOpen(true)}
    >
      <div className="flex h-3.5 w-5 flex-col justify-between">
        <span className="block h-0.5 w-full bg-foreground" />
        <span className="block h-0.5 w-full bg-foreground" />
        <span className="block h-0.5 w-full bg-foreground" />
      </div>
    </div>
  ) : null;

  const rightContent = isAuthenticated ? (
    <>
      {authenticatedContent}
      {mobileMenuButton}
    </>
  ) : authButtons;

  const contentPt = isMobile ? '64px' : '16px';
  const contentPb = isMobile ? '48px' : '64px';
  const contentPx = isMobile ? '20px' : '40px';

  const mobileNavItems = navData.map((item) => ({
    heading: item.titleKey ? t(item.titleKey) : item.title,
    href: item.path,
  }));

  return (
    <LayoutSection
      headerSection={
        <FloatingHeader
          scrolled={scrolled}
          leftContent={leftHeaderContent}
          rightContent={rightContent}
          className={header?.className}
        />
      }
      sidebarSection={
        <nav
          className="fixed left-0 top-0 z-[var(--layout-nav-zIndex)] hidden h-full w-[var(--layout-nav-vertical-width)] flex-col overflow-hidden md:flex"
        >
          <Sidebar open={navOpen || isDocked} setOpen={setNavOpen} isDocked={isDocked} setIsDocked={setIsDocked}>
            <SidebarBody className="py-0 justify-between gap-6 bg-white dark:bg-zinc-950">
              <NavContent 
                data={navData} 
                showHeader 
                collapsed={!navOpen && !isDocked} 
                isDocked={isDocked} 
                onToggleDock={() => setIsDocked(!isDocked)} 
              />
            </SidebarBody>
          </Sidebar>
        </nav>
      }
      footerSection={null}
      cssVars={{
        '--layout-nav-vertical-width': navOpen || isDocked ? '300px' : '60px',
        '--layout-dashboard-content-pt': contentPt,
        '--layout-dashboard-content-pb': contentPb,
        '--layout-dashboard-content-px': contentPx,
      }}
      className={className}
    >
      <LocationStateHandler />

      <Main>{children}</Main>

      {isMobile && (
        <CurvedMenu
          isActive={menuOpen}
          setIsActive={setMenuOpen}
          navItems={mobileNavItems}
        />
      )}
    </LayoutSection>
  );
}
